import React, { useState, useEffect, useCallback } from 'react';
import { KeyRound, UserPlus, Trash2, Loader2, Crown, Copy, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—'; } catch (e) { return '—'; } };

const InviteResult = ({ invite, onDone }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => { try { await navigator.clipboard.writeText(invite.activation_code); setCopied(true); } catch (e) { /* noop */ } };
    return (
        <div className="p-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 space-y-2" data-testid="admin-invite-result">
            <p className="text-sm text-emerald-200">Codice di attivazione per <strong>{invite.email}</strong> (valido {invite.minutes} minuti, monouso):</p>
            <div className="flex items-center gap-2">
                <code className="flex-1 text-center text-lg font-mono font-bold tracking-wider text-white bg-slate-950 border border-slate-700 rounded-lg py-2" data-testid="admin-invite-code">{invite.activation_code}</code>
                <button onClick={copy} className="p-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700" title="Copia" data-testid="admin-invite-copy">{copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}</button>
            </div>
            <p className="text-xs text-slate-400">Comunicalo alla persona in modo sicuro (a voce o di persona). Dovrà aprire /admin, inserire la sua email e questo codice, poi scansionare il QR con l'app di autenticazione. Il codice non verrà più mostrato.</p>
            <button onClick={onDone} className="text-xs text-slate-400 hover:text-white" data-testid="admin-invite-done">Chiudi</button>
        </div>
    );
};

export const AdminManager = ({ apiBase, getToken }) => {
    const [data, setData] = useState({ admins: [], invites: [], isSuper: false });
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [invite, setInvite] = useState(null);

    const api = useCallback(async (path, opts = {}) => {
        const r = await fetch(`${apiBase}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` } });
        return { ok: r.ok, data: await r.json().catch(() => ({})) };
    }, [apiBase, getToken]);

    const load = useCallback(async () => {
        const r = await api('/api/v1/admin/admins');
        if (r.ok) setData(r.data);
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const doInvite = async () => {
        setErr('');
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setErr('Email non valida.'); return; }
        setLoading(true);
        const r = await api('/api/v1/admin/admins/invite', { method: 'POST', body: JSON.stringify({ email: email.trim() }) });
        setLoading(false);
        if (r.ok) { setInvite(r.data); setEmail(''); load(); } else setErr(r.data.detail || 'Operazione non riuscita.');
    };
    const remove = async (e) => {
        if (!window.confirm(`Rimuovere l'accesso Admin di ${e}?`)) return;
        const r = await api(`/api/v1/admin/admins/${encodeURIComponent(e)}`, { method: 'DELETE' });
        if (!r.ok) setErr(r.data.detail || 'Rimozione non riuscita.');
        load();
    };

    return (
        <main className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="admin-manager-panel">
            <div>
                <h2 className="text-xl font-semibold text-white flex items-center gap-2"><KeyRound className="w-5 h-5 text-indigo-400" /> Amministratori</h2>
                <p className="text-sm text-slate-400">Nessuno può registrarsi da solo: solo il super admin crea gli accessi con un codice monouso.</p>
            </div>

            {data.isSuper ? (
                <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 space-y-3" data-testid="admin-invite-box">
                    <p className="text-sm font-medium text-slate-200 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Aggiungi amministratore</p>
                    <div className="flex gap-2">
                        <input value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && doInvite()} placeholder="nome.cognome@86bit.it"
                            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500" data-testid="admin-invite-email-input" />
                        <button onClick={doInvite} disabled={loading} className="px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60" data-testid="admin-invite-btn">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Genera codice
                        </button>
                    </div>
                    {invite && <InviteResult invite={invite} onDone={() => setInvite(null)} />}
                </div>
            ) : (
                <p className="text-sm text-amber-300 flex items-center gap-2" data-testid="admin-not-super"><AlertTriangle className="w-4 h-4" /> Solo il super admin può aggiungere o rimuovere amministratori.</p>
            )}

            {err && <p className="text-sm text-red-400" data-testid="admin-invite-error">{err}</p>}
            <div className="space-y-2" data-testid="admin-list">
                {data.admins.filter(a => a.totp_enabled).map(a => (
                    <div key={a.email} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-900 border border-slate-800" data-testid={`admin-row-${a.email}`}>
                        <div className="min-w-0">
                            <p className="text-sm text-white font-medium flex items-center gap-2 truncate">{a.super && <Crown className="w-4 h-4 text-amber-400" />}{a.email}</p>
                            <p className="text-xs text-slate-500">{a.super ? 'Super admin' : 'Amministratore'} · attivato {fmt(a.activated_at)} · ultimo accesso {fmt(a.last_login)}</p>
                        </div>
                        {data.isSuper && !a.super && (
                            <button onClick={() => remove(a.email)} className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10" title="Rimuovi" data-testid={`admin-remove-${a.email}`}><Trash2 className="w-4 h-4" /></button>
                        )}
                    </div>
                ))}
                {data.invites.map(i => (
                    <div key={i.email} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-900/60 border border-dashed border-slate-700" data-testid={`admin-invite-row-${i.email}`}>
                        <div>
                            <p className="text-sm text-slate-300">{i.email}</p>
                            <p className="text-xs text-amber-300/80 flex items-center gap-1"><Clock className="w-3 h-3" /> In attesa di attivazione · scade {fmt(i.expires_at)}</p>
                        </div>
                        {data.isSuper && <button onClick={() => remove(i.email)} className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10" title="Annulla invito" data-testid={`admin-invite-cancel-${i.email}`}><Trash2 className="w-4 h-4" /></button>}
                    </div>
                ))}
            </div>
        </main>
    );
};

export default AdminManager;
